/**
 * Yanchi Collection JS
 * - Infinite scroll on main product grid
 * - When on a reel tag URL, also loads the full collection below via a second grid
 * - On normal collection pages, once that collection's own products end, continues
 *   into a clearly-labelled "More from Yanchi" grid pulling from the full catalogue
 */
(function () {
  'use strict';

  const RESTORE_KEY = 'yanchiCollectionReturn';
  const INITIAL_VIEWPORT_FILL_LIMIT = 2;
  const INITIAL_VIEWPORT_FILL_DELAY = 80;
  const SCROLL_KEYS = new Set(['ArrowDown', 'End', 'PageDown', ' ']);
  const CARD_IMAGE_SIZES = '(min-width: 1200px) 275px, (min-width: 990px) calc((100vw - 100px) / 4), (min-width: 750px) calc((100vw - 72px) / 3), calc((100vw - 36px) / 2)';
  const CARD_IMAGE_WIDTHS = [360, 480, 600, 800];
  let scrollFallbackArmed = false;
  let scrollFallbackIntentBound = false;

  class YanchiInfiniteScroll {
    constructor({ grid, sentinel, collectionUrl, skipIds = [], nextStage = null, preserveCurrentSearch = false }) {
      this.grid = grid;
      this.rootGrid = grid;
      this.sentinel = sentinel;
      this.sectionId = grid.dataset.sectionId;
      this.collectionUrl = collectionUrl || window.location.pathname;
      this.preserveCurrentSearch = preserveCurrentSearch;
      this.skipIds = new Set(skipIds.map(String));
      this.currentPage = parseInt(grid.dataset.currentPage || '1', 10);
      this.totalPages = parseInt(grid.dataset.totalPages || '99', 10);
      this.loading = false;
      this.destroyed = false;
      this.abortController = null;
      this.initialViewportFillCount = 0;
      // { grid, collectionUrl, divider } — swapped to once this stage is exhausted
      this.nextStage = nextStage;

      if (this.currentPage >= this.totalPages) {
        void this.advanceOrEnd();
        return;
      }

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '300px',
      });
      this.observer.observe(this.sentinel);
    }

    onIntersect(entries) {
      if (!entries[0].isIntersecting || this.loading) return;
      this.loadMore();
    }

    async loadMore() {
      if (this.destroyed || this.loading || this.sentinel.classList.contains('end')) return;

      this.loading = true;
      this.sentinel.classList.add('loading');
      const controller = new AbortController();
      this.abortController = controller;

      const nextPage = this.currentPage + 1;

      try {
        const url = new URL(this.collectionUrl, window.location.origin);
        if (this.preserveCurrentSearch) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.forEach((value, key) => {
            if (key !== 'page' && key !== 'section_id') url.searchParams.set(key, value);
          });
        }
        url.searchParams.set('page', nextPage);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error('Network error');

        const html = await response.text();
        if (this.destroyed) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newItems = doc.querySelectorAll('.yanchi-card-wrapper');

        let added = 0;
        newItems.forEach((item) => {
          const pid = item.dataset.productId;
          if (!this.skipIds.has(pid)) {
            this.grid.appendChild(item);
            this.skipIds.add(pid);
            added++;
          }
        });

        this.currentPage = nextPage;

        if (newItems.length === 0 || nextPage >= this.totalPages) {
          await this.advanceOrEnd();
          return;
        }

        // Await skipped pages so loading state cannot be cleared during the next request.
        if (added === 0) {
          this.loading = false;
          await this.loadMore();
          return;
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('[Yanchi] Infinite scroll error:', e);
        this.markEnd();
      } finally {
        if (this.abortController === controller) this.abortController = null;
        if (!this.destroyed) {
          this.loading = false;
          this.sentinel.classList.remove('loading');
          this.scheduleInitialViewportFill();
        }
      }
    }

    async advanceOrEnd() {
      if (this.nextStage) {
        const stage = this.nextStage;
        this.nextStage = null;

        if (stage.divider) stage.divider.hidden = false;

        this.grid = stage.grid;
        this.collectionUrl = stage.collectionUrl;
        this.preserveCurrentSearch = Boolean(stage.preserveCurrentSearch);
        this.sectionId = stage.grid.dataset.sectionId || this.sectionId;
        this.currentPage = 0;
        this.totalPages = 99;
        this.loading = false;
        this.initialViewportFillCount = 0;
        await this.loadMore();
        return;
      }
      this.markEnd();
    }

    scheduleInitialViewportFill() {
      if (
        this.initialViewportFillCount >= INITIAL_VIEWPORT_FILL_LIMIT
        || !shouldFillInitialViewport(this.sentinel)
        || this.sentinel.classList.contains('end')
      ) return;

      this.initialViewportFillCount += 1;
      window.setTimeout(() => {
        if (!this.destroyed && !this.loading && shouldFillInitialViewport(this.sentinel)) {
          void this.loadMore();
        }
      }, INITIAL_VIEWPORT_FILL_DELAY);
    }

    destroy() {
      this.destroyed = true;
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
      this.sentinel.classList.remove('loading');
    }

    markEnd() {
      if (this.observer) this.observer.disconnect();
      this.sentinel.classList.remove('loading');
      this.sentinel.classList.add('end');
    }
  }

  class YanchiReelViewScroll {
    constructor({ grid, sentinel, apiUrl, skipIds = [], pageSize = 24 }) {
      this.grid = grid;
      this.rootGrid = grid;
      this.sentinel = sentinel;
      this.apiUrl = apiUrl;
      this.pageSize = pageSize;
      this.skipIds = new Set(skipIds.map(String));
      this.currentPage = 0;
      this.hasNextPage = true;
      this.loading = false;
      this.lastGroupKey = '';
      this.destroyed = false;
      this.abortController = null;
      this.initialViewportFillCount = 0;

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '300px',
      });
      this.observer.observe(this.sentinel);
      this.loadMore();
    }

    onIntersect(entries) {
      if (!entries[0].isIntersecting || this.loading || !this.hasNextPage) return;
      this.loadMore();
    }

    async loadMore() {
      if (this.destroyed || this.loading || !this.hasNextPage || this.sentinel.classList.contains('end')) return;

      this.loading = true;
      this.sentinel.classList.add('loading');
      const controller = new AbortController();
      this.abortController = controller;

      try {
        const url = new URL(this.apiUrl);
        url.searchParams.set('page', String(this.currentPage + 1));
        url.searchParams.set('pageSize', String(this.pageSize));
        const response = await fetch(url.toString(), { mode: 'cors', signal: controller.signal });
        if (!response.ok) throw new Error('Network error');
        const payload = await response.json();
        if (this.destroyed) return;
        if (!payload.ok) throw new Error(payload.reason || 'Reel view unavailable');

        let added = 0;
        (payload.products || []).forEach((product) => {
          const id = String(product.legacyProductId || product.shopifyProductId || '');
          if (!id || this.skipIds.has(id)) return;
          const groupKey = product.group === 'priority-tag'
            ? 'priority:' + (product.priorityTag || '')
            : product.group || 'all';
          if (groupKey !== this.lastGroupKey) {
            const divider = renderGroupDivider(product);
            if (divider) this.grid.appendChild(divider);
            this.lastGroupKey = groupKey;
          }
          this.grid.appendChild(renderApiProductCard(product));
          this.skipIds.add(id);
          if (product.shopifyProductId) this.skipIds.add(String(product.shopifyProductId));
          added++;
        });

        this.currentPage = Number(payload.page || this.currentPage + 1);
        this.hasNextPage = Boolean(payload.hasNextPage);

        if (!this.hasNextPage) {
          this.markEnd();
          return;
        }

        if (added === 0) {
          this.loading = false;
          await this.loadMore();
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Yanchi] Reel view scroll error:', error);
        this.markEnd();
      } finally {
        if (this.abortController === controller) this.abortController = null;
        if (!this.destroyed) {
          this.loading = false;
          this.sentinel.classList.remove('loading');
          this.scheduleInitialViewportFill();
        }
      }
    }

    scheduleInitialViewportFill() {
      if (
        this.initialViewportFillCount >= INITIAL_VIEWPORT_FILL_LIMIT
        || !shouldFillInitialViewport(this.sentinel)
        || this.sentinel.classList.contains('end')
      ) return;

      this.initialViewportFillCount += 1;
      window.setTimeout(() => {
        if (!this.destroyed && !this.loading && shouldFillInitialViewport(this.sentinel)) {
          void this.loadMore();
        }
      }, INITIAL_VIEWPORT_FILL_DELAY);
    }

    destroy() {
      this.destroyed = true;
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
      this.sentinel.classList.remove('loading');
    }

    markEnd() {
      if (this.observer) this.observer.disconnect();
      this.sentinel.classList.remove('loading');
      this.sentinel.classList.add('end');
    }
  }

  function init() {
    const existingInstance = window.__yanchiInfiniteScrollInstance;
    const sentinel = document.querySelector('.yanchi-scroll-sentinel');
    if (!sentinel) {
      if (existingInstance && typeof existingInstance.destroy === 'function') existingInstance.destroy();
      window.__yanchiInfiniteScrollInstance = null;
      if (window.__yanchiInfiniteScrollScrollHandler) {
        window.removeEventListener('scroll', window.__yanchiInfiniteScrollScrollHandler);
        window.__yanchiInfiniteScrollScrollHandler = null;
      }
      return;
    }

    const mainGrid = document.getElementById('product-grid');
    const moreGrid = document.getElementById('more-product-grid'); // reel pages: rest of full catalogue
    const moreYanchiGrid = document.getElementById('more-yanchi-grid'); // normal pages: cross-collection continuation
    const moreYanchiDivider = document.getElementById('more-yanchi-divider');
    const targetGrid = moreGrid || mainGrid;

    if (
      existingInstance
      && !existingInstance.destroyed
      && existingInstance.rootGrid === targetGrid
      && existingInstance.sentinel === sentinel
    ) {
      bindScrollFallback();
      return;
    }

    if (existingInstance && typeof existingInstance.destroy === 'function') existingInstance.destroy();

    if (moreGrid) {
      // Reel page: main grid has reel products (already rendered server-side),
      // more grid loads the configured reel view order, skipping reel IDs.
      const skipIds = (moreGrid.dataset.skipIds || '').split(',').filter(Boolean);
      const reelApi = moreGrid.dataset.reelApi;

      window.__yanchiInfiniteScrollInstance = reelApi
        ? new YanchiReelViewScroll({
            grid: moreGrid,
            sentinel,
            apiUrl: reelApi,
            skipIds,
            pageSize: parseInt(moreGrid.dataset.pageSize || '24', 10) || 24,
          })
        : new YanchiInfiniteScroll({
            grid: moreGrid,
            sentinel,
            collectionUrl: moreGrid.dataset.collectionUrl || window.location.pathname.split('/').slice(0, -1).join('/'),
            skipIds,
          });
      bindScrollFallback();
      restoreProductPosition(window.__yanchiInfiniteScrollInstance);
    } else if (mainGrid) {
      // Normal collection page: paginate this collection first, then continue
      // into "More from Yanchi" (full catalogue) once it's exhausted.
      const skipIds = (mainGrid.dataset.skipIds || '').split(',').filter(Boolean);
      const nextStage = moreYanchiGrid
        ? {
            grid: moreYanchiGrid,
            collectionUrl: '/collections/all',
            divider: moreYanchiDivider,
            preserveCurrentSearch: false,
          }
        : null;

      window.__yanchiInfiniteScrollInstance = new YanchiInfiniteScroll({
        grid: mainGrid,
        sentinel,
        collectionUrl: window.location.pathname,
        skipIds,
        nextStage,
        preserveCurrentSearch: true,
      });
      bindScrollFallback();
      restoreProductPosition(window.__yanchiInfiniteScrollInstance);
    }

    bindProductReturnCapture();
  }

  function bindScrollFallback() {
    bindScrollFallbackIntent();

    if (window.__yanchiInfiniteScrollScrollHandler) {
      window.removeEventListener('scroll', window.__yanchiInfiniteScrollScrollHandler);
    }

    window.__yanchiInfiniteScrollScrollHandler = function () {
      const instance = window.__yanchiInfiniteScrollInstance;
      const sentinel = document.querySelector('.yanchi-scroll-sentinel');
      if (!instance || !sentinel || instance.loading || sentinel.classList.contains('end')) return;

      if (scrollFallbackArmed && isSentinelInLoadZone(sentinel)) {
        scrollFallbackArmed = false;
        instance.loadMore();
      }
    };

    window.addEventListener('scroll', window.__yanchiInfiniteScrollScrollHandler, { passive: true });
  }

  function bindScrollFallbackIntent() {
    if (scrollFallbackIntentBound) return;
    scrollFallbackIntentBound = true;

    const armFallback = function () {
      scrollFallbackArmed = true;
    };

    window.addEventListener('wheel', armFallback, { passive: true });
    window.addEventListener('touchstart', armFallback, { passive: true });
    window.addEventListener('pointerdown', armFallback, { passive: true });
    window.addEventListener('keydown', function (event) {
      if (SCROLL_KEYS.has(event.key)) armFallback();
    });
  }

  function isSentinelInLoadZone(sentinel) {
    const rect = sentinel.getBoundingClientRect();
    return rect.top < window.innerHeight + 800;
  }

  function shouldFillInitialViewport(sentinel) {
    if (window.scrollY > 1) return false;
    const rect = sentinel.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom >= 0;
  }

  function renderGroupDivider(product) {
    if (product.group === 'priority-tag' && product.priorityTag) {
      return dividerItem('More ' + product.priorityTag + ' styles');
    }
    if (product.group === 'all') return dividerItem('All products');
    return null;
  }

  function dividerItem(label) {
    const item = document.createElement('li');
    item.className = 'yanchi-reel-group-divider';
    item.innerHTML = '<span></span><strong></strong><span></span>';
    item.querySelector('strong').textContent = label;
    return item;
  }

  function renderApiProductCard(product) {
    const item = document.createElement('li');
    item.className = 'yanchi-card-wrapper';
    item.dataset.productId = String(product.legacyProductId || product.shopifyProductId || '');

    const href = product.handle ? '/products/' + encodeURIComponent(product.handle) : (product.url || '#');
    const image = product.imageUrl || '';
    const title = product.title || '';
    const price = money(product.price);
    const compareAt = Number(product.compareAtPrice || 0) > Number(product.price || 0) ? money(product.compareAtPrice) : '';
    const imageSrcset = shopifyImageSrcset(image);
    const responsiveImageAttributes = imageSrcset
      ? ' srcset="' + escapeHtml(imageSrcset) + '" sizes="' + CARD_IMAGE_SIZES + '"'
      : '';

    item.innerHTML =
      '<a class="yanchi-card" href="' + escapeHtml(href) + '" title="' + escapeHtml(title) + '" aria-label="' + escapeHtml(title) + '">' +
        '<div class="yanchi-card__media">' +
          (image
            ? '<img src="' + escapeHtml(image) + '"' + responsiveImageAttributes + ' alt="' + escapeHtml(title) + '" class="yanchi-card__img--primary" width="600" height="750" loading="lazy">'
            : '<div class="yanchi-card__placeholder" aria-hidden="true"></div>') +
        '</div>' +
        '<div class="yanchi-card__info">' +
          '<p class="yanchi-card__title">' + escapeHtml(cleanTitle(title)) + '</p>' +
          '<p class="yanchi-card__price">' +
            (compareAt ? '<span class="yanchi-card__price-now">' + price + '</span> <s class="yanchi-card__price-was">' + compareAt + '</s>' : price) +
          '</p>' +
        '</div>' +
      '</a>';
    return item;
  }

  function shopifyImageSrcset(value) {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const isShopifyHost = url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com');
      const isShopifyPath = url.pathname.includes('/s/files/') || url.pathname.includes('/cdn/shop/');
      if (!isShopifyHost || !isShopifyPath) return '';

      return CARD_IMAGE_WIDTHS.map(function (width) {
        const candidate = new URL(url.toString());
        candidate.searchParams.set('width', width);
        return candidate.toString() + ' ' + width + 'w';
      }).join(', ');
    } catch (error) {
      return '';
    }
  }

  function money(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return 'Rs. ' + number.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function cleanTitle(value) {
    return String(value || '').replace(/\s*[-|]\s*(Yanchi|Yanchi Studio)\s*$/i, '').trim();
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (char) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char];
    });
  }

  function pageKey() {
    return window.location.pathname + window.location.search;
  }

  function bindProductReturnCapture() {
    if (window.__yanchiProductReturnCaptureBound) return;
    window.__yanchiProductReturnCaptureBound = true;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    document.addEventListener('click', function (event) {
      const link = event.target.closest && event.target.closest('a.yanchi-card');
      if (!link) return;

      const wrapper = link.closest('.yanchi-card-wrapper');
      const productId = wrapper && wrapper.dataset.productId;
      if (!productId) return;

      try {
        window.sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
          key: pageKey(),
          productId: String(productId),
          scrollY: Math.max(0, window.scrollY || window.pageYOffset || 0),
          at: Date.now(),
        }));
      } catch (error) {
        // Storage can be unavailable in strict privacy modes; navigation should continue.
      }
    }, true);
  }

  async function restoreProductPosition(instance) {
    if (window.__yanchiProductReturnRestoreRunning || window.__yanchiProductReturnRestored) return;

    let state = null;
    try {
      state = JSON.parse(window.sessionStorage.getItem(RESTORE_KEY) || 'null');
    } catch (error) {
      state = null;
    }

    if (!state || state.key !== pageKey() || !state.productId || Date.now() - Number(state.at || 0) > 30 * 60_000) return;

    window.__yanchiProductReturnRestoreRunning = true;

    try {
      const target = await findOrLoadProduct(instance, String(state.productId));
      if (target) {
        scrollToProduct(target);
        window.__yanchiProductReturnRestored = true;
        window.sessionStorage.removeItem(RESTORE_KEY);
      } else if (Number.isFinite(Number(state.scrollY))) {
        window.scrollTo(0, Math.max(0, Number(state.scrollY)));
      }
    } finally {
      window.__yanchiProductReturnRestoreRunning = false;
    }
  }

  async function findOrLoadProduct(instance, productId) {
    const selector = `.yanchi-card-wrapper[data-product-id="${escapeSelectorValue(productId)}"]`;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const target = document.querySelector(selector);
      if (target) return target;

      const sentinel = document.querySelector('.yanchi-scroll-sentinel');
      if (!instance || !sentinel || sentinel.classList.contains('end')) return null;

      await instance.loadMore();
    }
    return document.querySelector(selector);
  }

  function scrollToProduct(target) {
    const switcher = document.querySelector('.yanchi-coll-switcher.is-pinned, .yanchi-coll-switcher');
    const switcherVisible = switcher && switcher.offsetParent !== null;
    const offset = switcherVisible ? Math.min(switcher.getBoundingClientRect().height + 14, 160) : 90;
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  function escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function observeFacetGridReplacements() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || window.__yanchiInfiniteScrollMutationObserver) return;

    window.__yanchiInfiniteScrollMutationObserver = new MutationObserver(function () {
      window.requestAnimationFrame(init);
    });
    window.__yanchiInfiniteScrollMutationObserver.observe(container, { childList: true });
  }

  window.YanchiInfiniteScroll = {
    init: init,
  };

  document.addEventListener('yanchi:collection-grid-updated', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      observeFacetGridReplacements();
    });
  } else {
    init();
    observeFacetGridReplacements();
  }
})();
